/**
 * Session administrateur — store Zustand **en mémoire uniquement** (DECISIONS §4.1, A-31).
 *
 * Deux invariants, et ils sont volontairement visibles ici :
 *
 * 1. **Le jeton d'accès n'est jamais persisté.** Aucun middleware `persist`, aucun appel à
 *    `localStorage`, `sessionStorage`, `document.cookie` ou `indexedDB`. Un rechargement de page
 *    vide le store et renvoie sur l'écran de connexion : c'est le comportement attendu, et il est
 *    prouvé par `src/session/session-admin.spec.ts`.
 * 2. **Le rôle affiché vient du serveur**, jamais d'une décision du client : `POST
 *    /admin/auth/login` ne délivre un jeton qu'à un compte `role = "admin"`, et l'interface se
 *    contente de constater la présence de ce jeton.
 */
import { create } from 'zustand';

/** Administrateur connecté, tel que renvoyé par le serveur. */
export interface AdminConnecte {
  readonly id: string;
  readonly email: string;
}

export interface EtatSessionAdmin {
  /** Jeton d'accès JWT, en mémoire seulement. `null` = aucune session. */
  readonly jeton: string | null;
  readonly admin: AdminConnecte | null;
  /** Message du dernier refus de connexion (le serveur a répondu, sans jeton). */
  readonly refus: 'compte-non-admin' | 'identifiants' | null;
  readonly definirSession: (session: { jeton: string; admin: AdminConnecte }) => void;
  readonly definirJeton: (jeton: string) => void;
  readonly definirRefus: (refus: EtatSessionAdmin['refus']) => void;
  readonly deconnecter: () => void;
}

/** Store unique de session. Aucun middleware `persist` : c'est volontaire et documenté. */
export const useSessionAdmin = create<EtatSessionAdmin>((set) => ({
  jeton: null,
  admin: null,
  refus: null,
  definirSession: ({ jeton, admin }) => set({ jeton, admin, refus: null }),
  definirJeton: (jeton) => set({ jeton }),
  definirRefus: (refus) => set({ refus }),
  deconnecter: () => set({ jeton: null, admin: null, refus: null }),
}));

/** Jeton courant, lu hors composant React (client HTTP). */
export function lireJeton(): string | null {
  return useSessionAdmin.getState().jeton;
}

/** Vrai si une session administrateur est ouverte (garde de route). */
export function estConnecte(): boolean {
  return useSessionAdmin.getState().jeton !== null;
}
